// Calendar days for the contract model. Its dates are "YYYY-MM-DD" strings on
// the wire, "" = not stated (never an instant, SAD-294), so everything here is
// string arithmetic on the day itself: nothing passes through a time zone.
const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
];
const WEEKDAYS = [
    'Sun',
    'Mon',
    'Tue',
    'Wed',
    'Thu',
    'Fri',
    'Sat'
];
const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
function parts(iso) {
    const m = ISO.exec(iso ?? '');
    if (!m) return null;
    return [
        Number(m[1]),
        Number(m[2]),
        Number(m[3])
    ];
}
function utcDay(iso) {
    const p = parts(iso);
    return p ? Date.UTC(p[0], p[1] - 1, p[2]) : null;
}
/** Today in the viewer's own zone, as the calendar day they are standing on. */ export function todayISO(now = new Date()) {
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}
/** "2 Feb 2026"; "" (not stated) reads as `absent`. */ export function cmDate(iso, absent = 'Not stated') {
    const p = parts(iso ?? '');
    if (!p) return absent;
    return `${p[2]} ${MONTHS[p[1] - 1]} ${p[0]}`;
}
/** "Mon 2 Feb 2026" — the wireframe's due line. */ export function cmDateLong(iso, absent = 'Not stated') {
    const t = utcDay(iso ?? '');
    if (t === null) return absent;
    return `${WEEKDAYS[new Date(t).getUTCDay()]} ${cmDate(iso)}`;
}
/** "2 Feb" — a table cell where the year is the column's context. */ export function cmDateShort(iso, absent = '—') {
    const p = parts(iso ?? '');
    if (!p) return absent;
    return `${p[2]} ${MONTHS[p[1] - 1]}`;
}
/** "February 2026" — a monthly date's period. */ export function cmMonth(iso) {
    const p = parts(iso ?? '');
    if (!p) return '';
    const long = new Date(Date.UTC(p[0], p[1] - 1, 1)).toLocaleString('en-GB', {
        month: 'long',
        timeZone: 'UTC'
    });
    return `${long} ${p[0]}`;
}
/** Whole days from `from` to `to` (negative when `to` is earlier); null when either is unstated. */ export function daysBetween(from, to) {
    const a = utcDay(from);
    const b = utcDay(to);
    if (a === null || b === null) return null;
    return Math.round((b - a) / 86400000);
}
/** `iso` plus n calendar days. */ export function addDays(iso, n) {
    const t = utcDay(iso);
    if (t === null) return '';
    return new Date(t + n * 86400000).toISOString().slice(0, 10);
}
/** "26 Jan 2026 – 25 Jan 2027"; an open end reads "onward" / "until …". */ export function cmSpan(start, end) {
    if (!start && !end) return 'No dates stated';
    if (!end) return `${cmDate(start)} onward`;
    if (!start) return `until ${cmDate(end)}`;
    return `${cmDate(start)} – ${cmDate(end)}`;
}
/** An RFC 3339 instant ("2026-03-03T14:02:00Z") as the day it happened, locally. */ export function instantDay(rfc3339) {
    if (!rfc3339) return '';
    const d = new Date(rfc3339);
    if (Number.isNaN(d.getTime())) return '';
    return todayISO(d);
}
